import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import type {
  EhrAllergyResidentHistoryItem,
  EhrAttachSwitches,
  EhrExclusion,
  EhrFrequencyList,
  EhrFrequencyToken,
  EhrMapping,
  EhrMappingHistoryEntry,
  EhrMappingHistoryKind,
  EhrMappingLookups,
  EhrMatchPreview,
  EhrRoomBedMap,
  EhrTargetLookups,
  EhrUnitMap,
} from '../types/ehrMappings';

// React Query hooks for EHR mapping maintenance (EhrMappingsController).

export const ehrMappingKeys = {
  all: ['ehrMappingMaintenance'] as const,
  lookups: (facilityId: number) => [...ehrMappingKeys.all, 'lookups', facilityId] as const,
  mappings: (facilityId: number) => [...ehrMappingKeys.all, 'mappings', facilityId] as const,
  exclusions: (facilityId: number) => [...ehrMappingKeys.all, 'exclusions', facilityId] as const,
  units: (facilityId: number) => [...ehrMappingKeys.all, 'units', facilityId] as const,
  roomBeds: (facilityId: number) => [...ehrMappingKeys.all, 'roomBeds', facilityId] as const,
  history: (kind: EhrMappingHistoryKind, facilityId: number, id: number) =>
    [...ehrMappingKeys.all, 'history', kind, facilityId, id] as const,
  deleted: (kind: EhrMappingHistoryKind, facilityId: number) =>
    [...ehrMappingKeys.all, 'deleted', kind, facilityId] as const,
  allergyResidents: (facilityId: number, value: string) =>
    [...ehrMappingKeys.all, 'allergyResidents', facilityId, value] as const,
  targetLookups: (facilityId: number) => [...ehrMappingKeys.all, 'targetLookups', facilityId] as const,
  switches: (facilityId: number) => [...ehrMappingKeys.all, 'switches', facilityId] as const,
  frequencies: (facilityId: number) => [...ehrMappingKeys.all, 'frequencies', facilityId] as const,
  matchPreview: (facilityId: number, body: string) => [...ehrMappingKeys.all, 'matchPreview', facilityId, body] as const,
};

const historyUrl: Record<EhrMappingHistoryKind, (facilityId: number, id: number) => string> = {
  mapping: API.ehrMapHistory,
  exclusion: API.ehrMapExclusionHistory,
  unit: API.ehrMapUnitHistory,
  roomBed: API.ehrMapRoomBedHistory,
};

const deletedUrl: Record<EhrMappingHistoryKind, (facilityId: number) => string> = {
  mapping: API.ehrMapDeleted,
  exclusion: API.ehrMapExclusionDeleted,
  unit: API.ehrMapUnitDeleted,
  roomBed: API.ehrMapRoomBedDeleted,
};

/** The message a failed call carries: the API's 400/404/409 text when it sent one. */
export function ehrMappingErrorMessage(error: unknown, fallback: string): string {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  const body = response?.data;
  if (typeof body === 'string' && body.trim()) return body;
  if (body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string') {
    return (body as { error: string }).error;
  }
  if (response?.status === 403) return 'Your role does not allow this.';
  return fallback;
}

// ===== LOOKUPS =====

export function useEhrMappingLookups(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: ehrMappingKeys.lookups(facilityId),
    queryFn: async () => (await apiClient.get<EhrMappingLookups>(API.ehrMapLookups(facilityId))).data,
    enabled: enabled && facilityId > 0,
    staleTime: 5 * 60 * 1000,
  });
}

// ===== MAPPINGS =====

export function useEhrMappingList(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: ehrMappingKeys.mappings(facilityId),
    queryFn: async () => (await apiClient.get<EhrMapping[]>(API.ehrMapList(facilityId))).data,
    enabled: enabled && facilityId > 0,
  });
}

export function useSaveEhrMapping(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mapping: EhrMapping) =>
      (await apiClient.post<number>(API.ehrMapAddMod(facilityId), mapping)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ehrMappingKeys.mappings(facilityId) });
      // A new supplement / tube feed mapping can take text off the waiting lists.
      qc.invalidateQueries({ queryKey: ehrMappingKeys.frequencies(facilityId) });
    },
  });
}

// ===== EHR TARGETS (phase 4) =====

/** 404 = an API slot older than the website: the target editors stay hidden. */
const missingRoute = (error: unknown) => (error as { response?: { status?: number } })?.response?.status === 404;

/** What mappings can give: global meal patterns (with their mealtimes), global adjustments, formulas, problem templates. */
export function useEhrTargetLookups(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: ehrMappingKeys.targetLookups(facilityId),
    queryFn: async () => (await apiClient.get<EhrTargetLookups>(API.ehrMapTargetLookups(facilityId), { _skipVersionMismatch: true } as never)).data,
    enabled: enabled && facilityId > 0,
    staleTime: 5 * 60 * 1000,
    retry: (count, error) => !missingRoute(error) && count < 2,
  });
}

/** The facility's four EHR target switches (read only here; PSI changes them in Tech Log). */
export function useEhrSwitches(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: ehrMappingKeys.switches(facilityId),
    queryFn: async () => (await apiClient.get<EhrAttachSwitches>(API.ehrMapSwitches(facilityId), { _skipVersionMismatch: true } as never)).data,
    enabled: enabled && facilityId > 0,
    staleTime: 5 * 60 * 1000,
    retry: (count, error) => !missingRoute(error) && count < 1,
  });
}

/** The frequency table, what waits on it, and waiting text whose mapping cannot attach it. */
export function useEhrFrequencies(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: ehrMappingKeys.frequencies(facilityId),
    queryFn: async () => (await apiClient.get<EhrFrequencyList>(API.ehrMapFrequencies(facilityId), { _skipVersionMismatch: true } as never)).data,
    enabled: enabled && facilityId > 0,
    retry: (count, error) => !missingRoute(error) && count < 2,
  });
}

/** Sets (mask > 0) or clears (0) what one frequency means here. */
export function useSaveEhrFrequency(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { token: string; mealtimesMask: number }) =>
      (await apiClient.post<EhrFrequencyToken>(API.ehrMapFrequencies(facilityId), body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ehrMappingKeys.frequencies(facilityId) });
      qc.invalidateQueries({ queryKey: ['ehrMapThis'] });
    },
  });
}

/** Which recent supplement / tube feed phrases a value would catch (null request: idle). */
export function useEhrMatchPreview(facilityId: number, request: { value: string; mappingType: number; pKey_Ehr_Mapping: number } | null) {
  const body = request ? JSON.stringify(request) : '';
  return useQuery({
    queryKey: ehrMappingKeys.matchPreview(facilityId, body),
    queryFn: async () => (await apiClient.post<EhrMatchPreview>(API.ehrMapMatchPreview(facilityId), request)).data,
    enabled: facilityId > 0 && !!request && request.value.trim().length > 0,
    staleTime: 60 * 1000,
    retry: (count, error) => !missingRoute(error) && count < 1,
  });
}

export function useDeleteEhrMapping(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(API.ehrMapDelete(facilityId, id));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ehrMappingKeys.mappings(facilityId) });
      qc.invalidateQueries({ queryKey: ehrMappingKeys.deleted('mapping', facilityId) });
    },
  });
}

// ===== EXCLUSIONS =====

export function useEhrExclusions(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: ehrMappingKeys.exclusions(facilityId),
    queryFn: async () => (await apiClient.get<EhrExclusion[]>(API.ehrMapExclusions(facilityId))).data,
    enabled: enabled && facilityId > 0,
  });
}

export function useSaveEhrExclusion(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: EhrExclusion) =>
      (await apiClient.post<number>(API.ehrMapExclusionAddMod(facilityId), row)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ehrMappingKeys.exclusions(facilityId) }),
  });
}

export function useDeleteEhrExclusion(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(API.ehrMapExclusionDelete(facilityId, id));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ehrMappingKeys.exclusions(facilityId) });
      qc.invalidateQueries({ queryKey: ehrMappingKeys.deleted('exclusion', facilityId) });
    },
  });
}

// ===== UNIT MAPPINGS =====

export function useEhrUnitMaps(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: ehrMappingKeys.units(facilityId),
    queryFn: async () => (await apiClient.get<EhrUnitMap[]>(API.ehrMapUnits(facilityId))).data,
    enabled: enabled && facilityId > 0,
  });
}

export function useSaveEhrUnitMap(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: EhrUnitMap) =>
      (await apiClient.post<number>(API.ehrMapUnitAddMod(facilityId), row)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ehrMappingKeys.units(facilityId) }),
  });
}

export function useDeleteEhrUnitMap(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(API.ehrMapUnitDelete(facilityId, id));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ehrMappingKeys.units(facilityId) });
      qc.invalidateQueries({ queryKey: ehrMappingKeys.deleted('unit', facilityId) });
    },
  });
}

// ===== ROOM / BED MAPPINGS =====

export function useEhrRoomBedMaps(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: ehrMappingKeys.roomBeds(facilityId),
    queryFn: async () => (await apiClient.get<EhrRoomBedMap[]>(API.ehrMapRoomBeds(facilityId))).data,
    enabled: enabled && facilityId > 0,
  });
}

export function useSaveEhrRoomBedMap(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (row: EhrRoomBedMap) =>
      (await apiClient.post<number>(API.ehrMapRoomBedAddMod(facilityId), row)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ehrMappingKeys.roomBeds(facilityId) }),
  });
}

export function useDeleteEhrRoomBedMap(facilityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(API.ehrMapRoomBedDelete(facilityId, id));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ehrMappingKeys.roomBeds(facilityId) });
      qc.invalidateQueries({ queryKey: ehrMappingKeys.deleted('roomBed', facilityId) });
    },
  });
}

// ===== HISTORY =====

/** The interaction log for one mapping (id), or null to stay idle. */
export function useEhrMappingHistory(kind: EhrMappingHistoryKind, facilityId: number, id: number | null) {
  return useQuery({
    queryKey: ehrMappingKeys.history(kind, facilityId, id ?? 0),
    queryFn: async () =>
      (await apiClient.get<EhrMappingHistoryEntry[]>(historyUrl[kind](facilityId, id ?? 0))).data,
    enabled: facilityId > 0 && (id ?? 0) > 0,
  });
}

export function useEhrDeletedMappings(kind: EhrMappingHistoryKind, facilityId: number, enabled: boolean) {
  return useQuery({
    queryKey: ehrMappingKeys.deleted(kind, facilityId),
    queryFn: async () => (await apiClient.get<EhrMappingHistoryEntry[]>(deletedUrl[kind](facilityId))).data,
    enabled: enabled && facilityId > 0,
  });
}

/** Processed messages that carried this allergy value (WinForms "Resident History"). */
export function useEhrAllergyResidentHistory(facilityId: number, value: string | null) {
  return useQuery({
    queryKey: ehrMappingKeys.allergyResidents(facilityId, value ?? ''),
    queryFn: async () =>
      (await apiClient.get<EhrAllergyResidentHistoryItem[]>(API.ehrMapAllergyResidentHistory(facilityId, value ?? ''))).data,
    enabled: facilityId > 0 && !!value,
  });
}
